/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Wizard Step 1: Parameters, Keywords & Brand Voice Setup (Task 10.2.3 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  useLongformWizardStore,
  ARTICLE_LENGTH_OPTIONS,
  DEFAULT_BRAND_VOICES,
  type ArticleLength,
} from '../../stores/useLongformWizardStore';
import { useI18n } from '../../stores/useI18nStore';
import {
  Tag,
  Key,
  FileText,
  Volume2,
  Globe,
  Plus,
  X,
  ArrowRight,
  Sparkles,
  HelpCircle,
} from 'lucide-react';

const SUPPORTED_LANGUAGES = [
  { code: 'vi', flag: '🇻🇳' },
  { code: 'en', flag: '🇺🇸' },
  { code: 'ja', flag: '🇯🇵' },
  { code: 'fr', flag: '🇫🇷' },
  { code: 'de', flag: '🇩🇪' },
  { code: 'es', flag: '🇪🇸' },
  { code: 'zh', flag: '🇨🇳' },
  { code: 'ko', flag: '🇰🇷' },
];

export const Step1SetupView: React.FC = () => {
  const {
    setup,
    updateSetup,
    addSecondaryKeyword,
    removeSecondaryKeyword,
    setStep,
    markStepCompleted,
    generateTitles,
  } = useLongformWizardStore();
  const { t } = useI18n();

  const [keywordInput, setKeywordInput] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleAddKeyword = () => {
    if (!keywordInput.trim()) return;
    const added = addSecondaryKeyword(keywordInput.trim());
    if (added) {
      setKeywordInput('');
      setErrorMsg(null);
    } else {
      setErrorMsg(t('wizard.step1Details.maxKeywordsHint'));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const handleContinue = () => {
    if (!setup.topic.trim()) {
      setErrorMsg(t('wizard.step1Details.validationTopic'));
      return;
    }
    if (!setup.primaryKeyword.trim()) {
      setErrorMsg(t('wizard.step1Details.validationKeyword'));
      return;
    }

    markStepCompleted(1);
    generateTitles();
    setStep(2);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* 1. Topic & Keywords Section */}
      <div className="p-6 rounded-2xl border border-surface-border bg-surface shadow-glass space-y-5">
        <div className="flex items-center gap-2 pb-3 border-b border-surface-border">
          <FileText className="w-5 h-5 text-primary" />
          <h3 className="font-heading font-bold text-base text-foreground">
            {t('wizard.step1Details.sectionTitle')}
          </h3>
        </div>

        {/* Topic Input */}
        <div className="space-y-1.5">
          <label className="block text-xs font-bold text-foreground uppercase tracking-wider">
            {t('wizard.step1Details.topicLabel')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={setup.topic}
            onChange={(e) => {
              updateSetup({ topic: e.target.value });
              if (errorMsg) setErrorMsg(null);
            }}
            placeholder={t('wizard.step1Details.topicPlaceholder')}
            className="w-full px-4 py-2.5 rounded-xl border border-surface-border bg-surface-elevated text-sm text-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all placeholder:text-foreground-muted/60"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
          {/* Primary Keyword */}
          <div className="space-y-1.5">
            <label className="flex items-center justify-between text-xs font-bold text-foreground uppercase tracking-wider">
              <span className="flex items-center gap-1">
                <Key className="w-3.5 h-3.5 text-accent" />
                <span>{t('wizard.step1Details.primaryKeywordLabel')}</span>
                <span className="text-red-500">*</span>
              </span>
            </label>
            <input
              type="text"
              value={setup.primaryKeyword}
              onChange={(e) => {
                updateSetup({ primaryKeyword: e.target.value });
                if (errorMsg) setErrorMsg(null);
              }}
              placeholder={t('wizard.step1Details.primaryKeywordPlaceholder')}
              className="w-full px-4 py-2.5 rounded-xl border border-surface-border bg-surface-elevated text-sm text-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all placeholder:text-foreground-muted/60"
            />
          </div>

          {/* Target Language */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-1 text-xs font-bold text-foreground uppercase tracking-wider">
              <Globe className="w-3.5 h-3.5 text-primary" />
              <span>{t('wizard.step1Details.languageLabel')}</span>
            </label>
            <select
              value={setup.language}
              onChange={(e) => updateSetup({ language: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl border border-surface-border bg-surface-elevated text-sm text-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {t(`languages.${lang.code}` as any)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Secondary Keywords (LSI) Tag Chip Input */}
        <div className="space-y-2 pt-2">
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-1.5 text-xs font-bold text-foreground uppercase tracking-wider">
              <Tag className="w-3.5 h-3.5 text-emerald-500" />
              <span>{t('wizard.step1Details.secondaryKeywordsLabel')}</span>
            </label>
            <span className="text-xs font-mono text-foreground-muted">
              {setup.secondaryKeywords.length}/8
            </span>
          </div>

          {/* Input field */}
          <div className="flex gap-2">
            <input
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('wizard.step1Details.secondaryKeywordsPlaceholder')}
              className="flex-1 px-4 py-2 rounded-xl border border-surface-border bg-surface-elevated text-xs text-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all placeholder:text-foreground-muted/60"
            />
            <button
              type="button"
              onClick={handleAddKeyword}
              className="flex items-center gap-1 px-3 py-2 rounded-xl bg-surface-elevated border border-surface-border text-xs font-semibold hover:bg-muted text-foreground transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>{t('wizard.step1Details.addKeywordBtn')}</span>
            </button>
          </div>

          {/* Tag Chips List */}
          {setup.secondaryKeywords.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {setup.secondaryKeywords.map((kw) => (
                <span
                  key={kw}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-primary/10 border border-primary/20 text-primary text-xs font-medium animate-scale-in"
                >
                  <span>{kw}</span>
                  <button
                    type="button"
                    onClick={() => removeSecondaryKeyword(kw)}
                    className="hover:text-red-500 transition-colors p-0.5 rounded-full hover:bg-white/10"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 2. Target Length Selector */}
      <div className="p-6 rounded-2xl border border-surface-border bg-surface shadow-glass space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-surface-border">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-accent" />
            <h3 className="font-heading font-bold text-base text-foreground">
              {t('wizard.step1Details.lengthLabel')}
            </h3>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {ARTICLE_LENGTH_OPTIONS.map((opt) => {
            const isSelected = setup.targetLength === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => updateSetup({ targetLength: opt.id as ArticleLength })}
                className={`relative p-4 rounded-xl border text-left flex flex-col justify-between transition-all duration-200 ${
                  isSelected
                    ? 'border-primary bg-primary/10 ring-1 ring-primary/40 shadow-glow-primary/20'
                    : 'border-surface-border bg-surface-elevated hover:border-surface-border/80'
                }`}
              >
                {opt.badge && (
                  <span className="absolute -top-2.5 right-3 px-2 py-0.5 rounded-full bg-accent text-accent-fg text-[10px] font-bold uppercase tracking-wider shadow-sm">
                    {opt.badge}
                  </span>
                )}
                <div>
                  <div className="text-xs font-bold text-foreground font-heading">
                    {opt.label}
                  </div>
                  <div className="text-lg font-extrabold text-primary font-heading mt-1">
                    ~{opt.approxWords.toLocaleString()} <span className="text-xs font-normal text-foreground-muted">{t('editor.words')}</span>
                  </div>
                </div>
                <p className="text-[11px] text-foreground-muted mt-2 leading-relaxed">
                  {opt.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* 3. Brand Voice & Tone Selector */}
      <div className="p-6 rounded-2xl border border-surface-border bg-surface shadow-glass space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-surface-border">
          <div className="flex items-center gap-2">
            <Volume2 className="w-5 h-5 text-emerald-500" />
            <h3 className="font-heading font-bold text-base text-foreground">
              {t('wizard.step1Details.brandVoiceLabel')}
            </h3>
          </div>
          <div className="flex items-center gap-1 text-xs text-foreground-muted">
            <HelpCircle className="w-3.5 h-3.5" />
            <span>Consistency Engine</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {DEFAULT_BRAND_VOICES.map((bv) => {
            const isSelected = setup.brandVoiceId === bv.id;
            return (
              <button
                key={bv.id}
                type="button"
                onClick={() => updateSetup({ brandVoiceId: bv.id })}
                className={`p-4 rounded-xl border text-left transition-all duration-200 flex flex-col justify-between ${
                  isSelected
                    ? 'border-emerald-500 bg-emerald-500/10 ring-1 ring-emerald-500/40 shadow-sm'
                    : 'border-surface-border bg-surface-elevated hover:border-surface-border/80'
                }`}
              >
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-bold text-foreground font-heading">
                      {bv.name}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-mono bg-muted text-foreground-muted">
                      {bv.tone}
                    </span>
                  </div>
                  <p className="text-[11px] text-foreground-muted leading-relaxed">
                    {bv.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Error Message */}
      {errorMsg && (
        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-xs font-medium animate-shake">
          {errorMsg}
        </div>
      )}

      {/* Action Footer */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={handleContinue}
          className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-fg text-sm font-bold hover:bg-primary-hover shadow-glow-primary/20 transition-all active:scale-95 cursor-pointer"
        >
          <span>{t('wizard.step1Details.continueBtn')}</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
